import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import type React from 'react';
import styles from './index.module.scss';

import { UserEnrollmentStatus } from '@/components/UserEnrollmentStatus';
import { DesignatedEnrollmentCoursesSettingSection } from '@/routes/_embaUserLayout/-components/DesignatedEnrollmentCoursesSettingSection';
import { EnrollmentCourseCard } from '@/routes/_embaUserLayout/-components/EnrollmentCourseCard';
import { ImportantNotificationBlock } from '@/routes/_embaUserLayout/-components/ImportantNotificationBlock';
import { LearnerPageTitle } from '@/routes/_embaUserLayout/-components/LearnerPageTitle';

export const USER_ENROLLMENT = graphql(/* GraphQL */ `
  fragment UserEnrollment on UserEnrollment {
    id
    startDateText
    endDateText
    scheduleStatus
    scheduleStatusText
    isTrial
    applicationType
    enrollmentCourses {
      id
    }
    nonDesignatedEnrollmentCourses {
      id
      ...EnrollmentCourseCard
    }
    designatedEnrollmentCoursesSettings {
      id
      userEnrollmentCourses {
        id
        ...EnrollmentCourseCard
      }
      ...DesignatedEnrollmentCoursesSettingSection
    }
  }
`);

export const IMPORTANT_NOTIFICATION = graphql(/* GraphQL */ `
  fragment EmbaImportantNotification on ImportantNotification {
    id
    description
    url
    publishAtFromText
  }
`);

export type UserEnrollmentPageProps = {
  currentEmbaUserEnrollment: FragmentType<typeof USER_ENROLLMENT>;
  importantNotification: FragmentType<typeof IMPORTANT_NOTIFICATION> | null;
};

export const UserEnrollmentPage = ({
  currentEmbaUserEnrollment,
  importantNotification,
}: UserEnrollmentPageProps) => {
  const embaUserEnrollmentCoursesData = useFragment(
    USER_ENROLLMENT,
    currentEmbaUserEnrollment,
  );
  const importantNotificationData = useFragment(
    IMPORTANT_NOTIFICATION,
    importantNotification,
  );
  const showEmbaOnlyTrialNotice =
    embaUserEnrollmentCoursesData.applicationType === 'emba_only_trial';
  const showHodaiPlusTrialNotice =
    embaUserEnrollmentCoursesData.applicationType === 'hodai_plus_trial';
  const hasNonDesignated =
    embaUserEnrollmentCoursesData.nonDesignatedEnrollmentCourses.length > 0;

  return (
    <div className={styles.embaUserEnrollmentCourses}>
      {importantNotificationData && (
        <ImportantNotificationBlock
          dateStr={importantNotificationData.publishAtFromText}
          description={importantNotificationData.description}
          url={importantNotificationData.url}
        />
      )}
      <ul className={styles.breadCrumb}>
        <li>科目一覧</li>
      </ul>
      <UserEnrollmentStatus
        scheduleStatus={embaUserEnrollmentCoursesData.scheduleStatus}
        scheduleStatusText={embaUserEnrollmentCoursesData.scheduleStatusText}
        enrollmentCoursesLength={
          embaUserEnrollmentCoursesData.enrollmentCourses.length
        }
        startDateText={embaUserEnrollmentCoursesData.startDateText}
        endDateText={embaUserEnrollmentCoursesData.endDateText}
      />
      <LearnerPageTitle title="科目一覧" />
      {showEmbaOnlyTrialNotice && (
        <div className={styles.embaOnlyTrialNotice}>
          <p className={styles.embaOnlyTrialNoticeTitle}>
            【eMBAトライアルのご利用について】
          </p>
          <div className={styles.embaOnlyTrialNoticeContent}>
            <div className={styles.embaOnlyTrialNoticeSection}>
              ・体験できる科目
              <br />
              {'　eMBAでご提供するすべての科目をご体験いただけます。'}
              <br />
              {'　（AI対話トレーニング科目、グロービスMBA科目、ショート科目）'}
            </div>
            <div className={styles.embaOnlyTrialNoticeSection}>
              ・学習できる範囲
              <br />
              {
                '　Webサイト経由で発行されたトライアルIDの場合、各科目の学習内容は一部に制限されています。'
              }
              <br />
              {'　全ての学習の体験をご希望の場合は'}
              <a
                href="https://pages.globis.co.jp/gdp_inquiry_emba.html"
                target="_blank"
                rel="noreferrer"
                className={styles.embaOnlyTrialNoticeLink}
              >
                こちら
              </a>
              よりお問い合わせください。
            </div>
          </div>
          <div className={styles.embaOnlyTrialNoticeAnnotation}>
            ※「AI対話トレーニング科目」は、GLOBIS 学び放題とeMBAのセットプラン「
            <a
              href="https://gce.globis.co.jp/service/hodai/plan/plus/"
              target="_blank"
              rel="noreferrer"
              className={styles.embaOnlyTrialNoticeLink}
            >
              GLOBIS 学び放題 プラス
            </a>
            」には含まれておりません。
          </div>
        </div>
      )}
      {showHodaiPlusTrialNotice && (
        <div className={styles.hodaiPlusTrialNotice}>
          <div className={styles.hodaiPlusTrialNoticeContent}>
            本トライアルIDでは、「GLOBIS 学び放題 プラス」として学習いただける、
            <br />
            eMBAの「グロービスMBA科目」「ショート科目」の合計14科目をご体験いただけます。
          </div>
          <div className={styles.hodaiPlusTrialNoticeAnnotation}>
            ※AIとの音声対話で対人スキルを鍛える「
            <a
              href="https://gce.globis.co.jp/service/training-type/e-learning/emba/curriculum/ai/"
              target="_blank"
              rel="noreferrer"
              className={styles.hodaiPlusTrialNoticeLink}
            >
              AI対話トレーニング科目
            </a>
            」は、「GLOBIS 学び放題 プラス」には含まれておりません。
            <br />
            {'　トライアルをご希望の場合は担当営業までお問い合わせください。'}
          </div>
        </div>
      )}
      {embaUserEnrollmentCoursesData.designatedEnrollmentCoursesSettings.map(
        (setting) => (
          <DesignatedEnrollmentCoursesSettingSection
            key={setting.id}
            setting={setting}
          >
            {setting.userEnrollmentCourses.map((course) => (
              <EnrollmentCourseCard key={course.id} course={course} />
            ))}
          </DesignatedEnrollmentCoursesSettingSection>
        ),
      )}
      {hasNonDesignated && (
        <ul className={styles.courseList}>
          {embaUserEnrollmentCoursesData.nonDesignatedEnrollmentCourses.map(
            (course) => (
              <EnrollmentCourseCard key={course.id} course={course} />
            ),
          )}
        </ul>
      )}
    </div>
  );
};
